import { isValid } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';

import { pluralize } from '@/food-editor/utils/utils';
import { parseApiTime, parseLocalTime } from '@/lib/dates';

/**
 *  Calls ``callback`` every ``interval`` ms, returning the new result.
 *
 *  For example::
 *
 *    function Counter() {
 *      const curCount = useInterval(1000, () => count++)
 *      return <div>Count: {curCount}</div>
 *    }
 *
 *  Would yield a counter which increments every second.
 */
export function useInterval<T>(ms: number, cb: () => T): T {
  const [res, setRes] = useState<T>();

  // Note: to make sure that the callback can be updated between calls,
  // we need to store it on an object that's local to this particular
  // component.

  const [cbObj, _] = useState<any>({});
  cbObj.cb = cb;

  useEffect(() => {
    setRes(cbObj.cb());
    const interval = setInterval(() => {
      setRes(cbObj.cb());
    }, ms);
    return () => clearInterval(interval);
  }, [ms, cbObj]);

  return res!;
}

/**
 * One shared 1s clock for all {@link HumanTime} cells. A foods/table page can
 * mount hundreds of HumanTimes; per-instance setInterval was a major lag source.
 */
const useHumanTimeClock = create<{ nowMs: number }>(() => ({
  nowMs: Date.now(),
}));

let humanTimeClockSubscribers = 0;
let humanTimeClockTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Ref-counted: the first mounted HumanTime starts the 1s tick and the last one
 * to unmount stops it, so no timer runs while no HumanTime is on screen.
 */
const subscribeHumanTimeClock = () => {
  humanTimeClockSubscribers += 1;
  if (humanTimeClockTimer == null) {
    useHumanTimeClock.setState({ nowMs: Date.now() });
    humanTimeClockTimer = setInterval(() => {
      useHumanTimeClock.setState({ nowMs: Date.now() });
    }, 1000);
  }
  return () => {
    humanTimeClockSubscribers -= 1;
    if (humanTimeClockSubscribers <= 0 && humanTimeClockTimer != null) {
      humanTimeClockSubscribers = 0;
      clearInterval(humanTimeClockTimer);
      humanTimeClockTimer = null;
    }
  };
};

/** Test hook: whether the shared clock interval is running. */
export const isHumanTimeClockRunning = () => humanTimeClockTimer != null;

/**
 * Bucket by the unit {@link formatHumanTime} displays so a cell re-renders only
 * when its label can change, not on every shared-clock tick.
 */
const humanTimeTickBucket = (nowMs: number, timeMs: number | null) => {
  if (timeMs == null) {
    return 0;
  }
  const deltaSec = Math.abs(nowMs - timeMs) / 1000;
  if (deltaSec < 60) {
    return `s${Math.floor(deltaSec)}`;
  }
  if (deltaSec < 60 * 60) {
    return `m${Math.floor(deltaSec / 60)}`;
  }
  if (deltaSec < 60 * 60 * 24) {
    return `h${Math.floor(deltaSec / 3600)}`;
  }
  return `d${Math.floor(deltaSec / 86400)}`;
};

export function formatHumanTime(value: string | number | Date | null, now?: Date, hideSeconds?: boolean): string {
  const time = value ? parseLocalTime(value) : null;
  if (!time || !isValid(time)) {
    return '–';
  }

  if (!now) {
    now = new Date();
  }

  const delta = (now.getTime() - time.getTime()) / 1000;
  const absDelta = Math.abs(delta);
  const suffix = delta > 0 ? 'ago' : 'from now';
  // Thresholds must use absDelta. Using signed delta made every future time
  // (delta < 0) match the "minutes" branch.
  // Stepped relative units (same idea as patient report TimeWithAge):
  // seconds < 1m, minutes < 1h, hours < 24h, then days.
  return absDelta < 10
    ? `just now`
    : absDelta < 60
      ? `${pluralize(Math.floor(absDelta), 'second')} ${suffix}`
      : absDelta < 60 * 60
        ? `${pluralize(Math.floor(absDelta / 60), 'minute')} ${suffix}`
        : absDelta < 60 * 60 * 24
          ? `${pluralize(Math.floor(absDelta / 60 / 60), 'hour')} ${suffix}`
          : `${pluralize(Math.floor(absDelta / 60 / 60 / 24), 'day')} ${suffix}`;
}

export function HumanTime(props: {
  value: string | number | Date | null | undefined;
  isUTC?: boolean;
  now?: Date;
  hideSeconds?: boolean;
}) {
  const time = useMemo(
    () => (props.value && props.isUTC ? parseApiTime(props.value) : props.value ? parseLocalTime(props.value) : null),
    [props.value, props.isUTC],
  );

  const timeMs = time?.getTime() ?? null;
  const frozenNow = props.now;

  const usesClock = !frozenNow;
  useEffect(() => (usesClock ? subscribeHumanTimeClock() : undefined), [usesClock]);

  // Subscribe at the coarseness the label needs so a 200-row × 5-column table
  // does not re-render every second for timestamps that only change hourly.
  // When `now` is passed (tests), skip the store by selecting a constant.
  useHumanTimeClock(state => (frozenNow ? 0 : humanTimeTickBucket(state.nowMs, timeMs)));

  // Before the clock is running (first mount after idle) its value may be stale.
  const now = frozenNow ?? new Date(humanTimeClockTimer != null ? useHumanTimeClock.getState().nowMs : Date.now());
  const humanTime = formatHumanTime(time, now, props.hideSeconds);

  if (!time || !isValid(time)) {
    return <span>–</span>;
  }

  return (
    <span className="whitespace-nowrap" title={time.toISOString().replace('T', ' ')}>
      {humanTime}
    </span>
  );
}
